/**
 * Volltextsuche in einer Richtlinie: über Nummer, Titel, geltenden Text und Entwurf jedes Abschnitts.
 *
 * - Begriffe durch Leerzeichen getrennt müssen alle vorkommen; „…" sucht die Wortfolge.
 * - Groß/klein egal; Umlaute auch umschrieben: „Pruefung" findet „Prüfung", „Strasse" „Straße".
 * - Treffer in Nummer und Titel zählen mehr als im Text; Fundstellen mit Ausschnitt.
 *
 * Läuft im Browser: der Arbeitsbereich hat die Texte aller Abschnitte schon geladen.
 */

export interface SearchableSection {
  _id: string;
  number: string;
  title: string;
  text: string;
  draftText?: string | null;
}

export interface SearchSnippet {
  /** Ausschnitt mit den Fundstellen als Bereiche [von, bis) darin. */
  text: string;
  marks: [number, number][];
}

export interface SearchResult {
  sectionId: string;
  number: string;
  title: string;
  score: number;
  hits: number;
  /** Der Treffer steht nur im Entwurf, nicht in der geltenden Fassung. */
  draftOnly: boolean;
  titleMarks: [number, number][];
  snippets: SearchSnippet[];
}

const FOLD: Record<string, string> = { ä: "ae", ö: "oe", ü: "ue", ß: "ss", Ä: "ae", Ö: "oe", Ü: "ue" };

/**
 * Text für den Vergleich: klein, Umlaute umschrieben, Leerraum einfach — mit Abbildung jeder
 * Position im gefalteten Text auf die im Original (für die Hervorhebung).
 */
export const foldText = (input: string): { folded: string; origin: number[] } => {
  // Zerlegte Umlaute (u + ¨) erst zusammensetzen, sonst würden sie zu „u“ statt „ue“. NFC ändert die
  // Länge nur bei solchen Folgen; die Abbildung auf das Original gilt dann für den zusammengesetzten Text.
  const text = input.normalize("NFC");
  let folded = "";
  const origin: number[] = [];
  let lastSpace = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (/\s/.test(ch)) {
      if (lastSpace) continue;
      folded += " ";
      origin.push(i);
      lastSpace = true;
      continue;
    }
    lastSpace = false;
    const rep = FOLD[ch] ?? ch.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
    for (const c of rep) {
      folded += c;
      origin.push(i);
    }
  }
  origin.push(text.length);
  return { folded, origin };
};

/** Suchbegriffe: Wörter und „Wortfolgen in Anführungszeichen", gefaltet; zu kurze entfallen. */
export const parseQuery = (query: string): string[] => {
  const terms: string[] = [];
  const re = /"([^"]+)"|„([^"“]+)[“"]|(\S+)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(query))) {
    const raw = (m[1] ?? m[2] ?? m[3] ?? "").trim();
    const term = foldText(raw).folded.trim();
    if (term.length >= 2 || /^\d$/.test(term)) terms.push(term);
  }
  return Array.from(new Set(terms));
};

/** Markdown auf lesbaren Text: Bilder, Tabellenstriche, Überschriftenzeichen, Hervorhebungen weg. */
export const plainText = (markdown: string): string =>
  String(markdown ?? "")
    .replace(/!\[[^\]]*\]\([^)]*\)/g, " ")
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/^\s*\|?\s*-{3,}[\s|:-]*$/gm, " ")
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/\|/g, " ")
    .replace(/\\([\\`*_[\]])/g, "$1")
    .replace(/[*_`]{1,3}/g, "")
    .replace(/\[\^\d+\]:?/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/** Alle Fundstellen der Begriffe im Original, zusammengeführt und sortiert. */
export const findMarks = (text: string, terms: string[]): [number, number][] => {
  const { folded, origin } = foldText(text);
  const marks: [number, number][] = [];
  for (const term of terms) {
    let at = folded.indexOf(term);
    while (at >= 0) {
      marks.push([origin[at], origin[at + term.length - 1] + 1]);
      at = folded.indexOf(term, at + term.length);
    }
  }
  marks.sort((a, b) => a[0] - b[0]);
  const merged: [number, number][] = [];
  for (const m of marks) {
    const last = merged[merged.length - 1];
    if (last && m[0] <= last[1]) last[1] = Math.max(last[1], m[1]);
    else merged.push([...m]);
  }
  return merged;
};

const SNIPPET = 70;

/** Ausschnitte um die Fundstellen, höchstens `max`, mit Wortgrenzen. */
const snippetsOf = (text: string, marks: [number, number][], max = 3): SearchSnippet[] => {
  const out: SearchSnippet[] = [];
  let i = 0;
  while (i < marks.length && out.length < max) {
    let from = Math.max(0, marks[i][0] - SNIPPET);
    let to = Math.min(text.length, marks[i][1] + SNIPPET);
    // Weitere Fundstellen im selben Ausschnitt mitnehmen.
    let j = i;
    while (j + 1 < marks.length && marks[j + 1][0] < to) j++;
    to = Math.min(text.length, Math.max(to, marks[j][1] + 20));
    if (from > 0) from = text.indexOf(" ", from) + 1 || from;
    if (to < text.length) to = text.lastIndexOf(" ", to) > marks[j][1] ? text.lastIndexOf(" ", to) : to;
    const prefix = from > 0 ? "… " : "";
    const suffix = to < text.length ? " …" : "";
    out.push({
      text: prefix + text.slice(from, to) + suffix,
      marks: marks.slice(i, j + 1).map(([a, b]) => [a - from + prefix.length, b - from + prefix.length] as [number, number]),
    });
    i = j + 1;
  }
  return out;
};

/** Vorkommen eines Begriffs; am Wortanfang zählt es voll, mitten im Wort zu einem Drittel. */
const count = (folded: string, term: string) => {
  let n = 0;
  for (let at = folded.indexOf(term); at >= 0; at = folded.indexOf(term, at + term.length)) n += at === 0 || !/[\p{L}\p{N}]/u.test(folded[at - 1]) ? 1 : 1 / 3;
  return n;
};

const positions = (folded: string, term: string) => {
  const out: number[] = [];
  for (let at = folded.indexOf(term); at >= 0; at = folded.indexOf(term, at + term.length)) out.push(at);
  return out;
};

/** Kleinster Abstand, in dem alle Begriffe vorkommen (Zeichen), oder Infinity. */
const nearest = (folded: string, terms: string[]) => {
  const lists = terms.map((t) => positions(folded, t));
  if (lists.some((l) => !l.length)) return Infinity;
  let best = Infinity;
  for (const at of lists[0]) {
    const span = Math.max(...lists.slice(1).map((l) => Math.min(...l.map((p) => Math.abs(p - at)))), 0);
    best = Math.min(best, span);
  }
  return best;
};

/** Abschnitte, in denen alle Begriffe vorkommen, nach Gewicht sortiert. */
export const searchSections = (sections: SearchableSection[], query: string, limit = 100): SearchResult[] => {
  const terms = parseQuery(query);
  if (!terms.length) return [];
  const results: SearchResult[] = [];
  for (const s of sections) {
    const heading = `${s.number} ${s.title}`;
    const current = plainText(s.text);
    const draft = s.draftText != null ? plainText(s.draftText) : null;
    const body = draft ?? current;
    const fHeading = foldText(heading).folded;
    const fBody = foldText(body).folded;
    const fCurrent = foldText(current).folded;
    if (!terms.every((t) => fHeading.includes(t) || fBody.includes(t))) continue;
    let score = 0;
    let hits = 0;
    for (const t of terms) {
      const inNumber = foldText(s.number).folded === t ? 20 : 0;
      const inTitle = count(foldText(s.title).folded, t);
      const inBody = count(fBody, t);
      hits += positions(foldText(s.title).folded, t).length + positions(fBody, t).length;
      // Häufigkeit zählt gedämpft: ein Abschnitt, der ein Wort oft nennt, ist nicht deshalb einschlägiger.
      score += inNumber + inTitle * 8 + Math.sqrt(Math.min(inBody, 25)) * 2 + (inBody > 0 ? 2 : 0);
    }
    // Mehrere Begriffe nah beieinander: je näher, desto mehr.
    if (terms.length > 1) {
      const gap = nearest(fBody, terms);
      if (fBody.includes(terms.join(" "))) score += 12;
      else if (gap <= 40) score += 10;
      else if (gap <= 120) score += 5;
    }
    const bodyMarks = findMarks(body, terms);
    results.push({
      sectionId: s._id,
      number: s.number,
      title: s.title,
      score,
      hits,
      draftOnly: draft != null && !terms.every((t) => fHeading.includes(t) || fCurrent.includes(t)),
      titleMarks: findMarks(s.title, terms),
      snippets: snippetsOf(body, bodyMarks),
    });
  }
  return results.sort((a, b) => b.score - a.score || a.number.localeCompare(b.number, "de", { numeric: true })).slice(0, limit);
};
